import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { 
    Building2, Wrench, Truck, ShieldCheck, 
    Users, CheckCircle2, AlertTriangle, Search, 
    Plus, Edit, Trash2, User, CornerDownRight, 
    Briefcase, XCircle, Check
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const CompanyDirectory = ({ onSelectCompany }) => {
    const [companies, setCompanies] = useState([]);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    
    // Form State
    const [formData, setFormData] = useState({ 
        id: null, 
        name: '', type: 'CONT', contact_person: '', email: '', 
        parent_company: '', site_ids: [] 
    }); 
    
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [compRes, siteRes] = await Promise.all([
                api.get('companies/'),
                api.get('sites/')
            ]);
            setCompanies(compRes.data);
            setSites(siteRes.data);
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    // --- HANDLERS ---

    const openCreateForm = () => {
        setFormData({ id: null, name: '', type: 'CONT', contact_person: '', email: '', parent_company: '', site_ids: [] });
        setShowForm(true);
    };

    const openEditForm = (company) => {
        setFormData({
            id: company.id,
            name: company.name,
            type: company.type,
            contact_person: company.contact_person || '',
            email: company.email || '',
            parent_company: company.parent_company || '',
            site_ids: company.working_site_ids || [] 
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (formData.id) {
                await api.patch(`companies/${formData.id}/`, formData);
                alert("Company Updated Successfully");
            } else {
                await api.post('companies/', formData);
                alert("Company Created Successfully");
            }
            setShowForm(false);
            fetchData();
        } catch (err) { 
            console.error(err);
            alert("Operation failed"); 
        }
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm("Delete this company? This removes all their users and docs.")) return;
        try {
            await api.delete(`companies/${id}/`);
            setCompanies(prev => prev.filter(c => c.id !== id));
        } catch (err) { alert("Error deleting company."); }
    };

    const toggleSite = (siteId) => {
        setFormData(prev => {
            const current = prev.site_ids;
            if (current.includes(siteId)) {
                return { ...prev, site_ids: current.filter(id => id !== siteId) };
            } else {
                return { ...prev, site_ids: [...current, siteId] };
            }
        });
    };

    // --- RENDER HELPERS ---
    const filteredCompanies = useMemo(() => {
        return companies.filter(c => {
            const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStatus = statusFilter === 'ALL' || c.readiness === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [companies, searchQuery, statusFilter]);

    const potentialParents = useMemo(() => companies.filter(c => c.type === 'CONT'), [companies]);

    const stats = useMemo(() => ({
        total: companies.length,
        compliant: companies.filter(c => c.readiness === 'GREEN').length,
        pending: companies.filter(c => c.readiness === 'YELLOW').length
    }), [companies]);

    const ReadinessBadge = ({ status }) => {
        if (status === 'GREEN') return <span style={{background:'rgba(16, 185, 129, 0.15)', color: THEME.success, border:`1px solid ${THEME.success}`, padding:'2px 8px', borderRadius:'12px', fontSize:'0.75rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:'4px'}}><CheckCircle2 size={12}/> Compliant</span>;
        if (status === 'YELLOW') return <span style={{background:'rgba(250, 204, 21, 0.15)', color: THEME.warning, border:`1px solid ${THEME.warning}`, padding:'2px 8px', borderRadius:'12px', fontSize:'0.75rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:'4px'}}><AlertTriangle size={12}/> Review</span>;
        return <span style={{background:'rgba(239, 68, 68, 0.15)', color: THEME.danger, border:`1px solid ${THEME.danger}`, padding:'2px 8px', borderRadius:'12px', fontSize:'0.75rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:'4px'}}><XCircle size={12}/> Incomplete</span>;
    };

    const getTypeIcon = (type) => {
        switch(type) {
            case 'CONT': return <Building2 size={24} color={THEME.primary} />;
            case 'SUB': return <Wrench size={24} color={THEME.warning} />;
            case 'SUPPLIER': return <Truck size={24} color={THEME.success} />;
            case 'OWNER': return <ShieldCheck size={24} color="#a855f7" />;
            default: return <Briefcase size={24} color={THEME.textSec} />;
        }
    };

    const getTypeLabel = (type) => {
        switch(type) {
            case 'CONT': return 'Contractor';
            case 'SUB': return 'Sub-Contractor';
            case 'SUPPLIER': return 'Supplier';
            case 'OWNER': return 'Site Owner';
            default: return 'Company';
        }
    };

    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', fontSize:'0.9rem', marginBottom:'15px', boxSizing:'border-box' };

    if (loading) return <div style={{color:'white'}}>Loading Directory...</div>;

    return (
        <div className="animate-fade-in">
            
            {/* STATS BAR */}
            <div style={{display:'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap:'20px', marginBottom:'30px'}}>
                <div style={{background: THEME.card, padding:'15px', borderRadius:'8px', borderLeft:`4px solid ${THEME.primary}`, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                    <div>
                        <div style={{color:THEME.textSec, fontSize:'0.8rem', fontWeight:'bold'}}>Total Companies</div>
                        <div style={{fontSize:'1.5rem', fontWeight:'bold', color:'white'}}>{stats.total}</div>
                    </div>
                    <Users size={24} color={THEME.primary} style={{opacity:0.8}}/>
                </div>
                <div style={{background: THEME.card, padding:'15px', borderRadius:'8px', borderLeft:`4px solid ${THEME.success}`, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                    <div>
                        <div style={{color:THEME.textSec, fontSize:'0.8rem', fontWeight:'bold'}}>Fully Compliant</div>
                        <div style={{fontSize:'1.5rem', fontWeight:'bold', color:THEME.success}}>{stats.compliant}</div>
                    </div>
                    <CheckCircle2 size={24} color={THEME.success} style={{opacity:0.8}}/>
                </div>
                <div style={{background: THEME.card, padding:'15px', borderRadius:'8px', borderLeft:`4px solid ${THEME.warning}`, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                    <div>
                        <div style={{color:THEME.textSec, fontSize:'0.8rem', fontWeight:'bold'}}>Pending Review</div>
                        <div style={{fontSize:'1.5rem', fontWeight:'bold', color:THEME.warning}}>{stats.pending}</div>
                    </div>
                    <AlertTriangle size={24} color={THEME.warning} style={{opacity:0.8}}/>
                </div>
            </div>

            {/* TOOLBAR */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', flexWrap:'wrap', gap:'15px'}}>
                <div style={{display:'flex', gap:'10px', flex:1, alignItems:'center'}}>
                    <div style={{position:'relative', width:'100%', maxWidth:'300px'}}>
                        <Search size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/>
                        <input 
                            type="text" 
                            placeholder="Search Companies..." 
                            value={searchQuery} 
                            onChange={(e) => setSearchQuery(e.target.value)} 
                            style={{...inputStyle, marginBottom:0, paddingLeft:'35px'}} 
                        />
                    </div>
                    <select 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)} 
                        style={{...inputStyle, marginBottom:0, maxWidth:'150px'}}
                    >
                        <option value="ALL">All Statuses</option>
                        <option value="GREEN">Compliant</option>
                        <option value="YELLOW">Pending</option>
                        <option value="RED">Incomplete</option>
                    </select>
                </div>
                <button onClick={openCreateForm} style={{padding:'10px 20px', background: THEME.primary, color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px'}}>
                    {showForm && !formData.id ? <XCircle size={18}/> : <Plus size={18}/>}
                    {showForm && !formData.id ? 'Cancel' : 'Add Company'}
                </button>
            </div>

            {/* CREATE / EDIT FORM */}
            {showForm && (
                <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', border:`1px solid ${THEME.primary}`, marginBottom:'30px'}}>
                    <h3 style={{color:'white', marginTop:0, display:'flex', alignItems:'center', gap:'10px'}}>
                        {formData.id ? <Edit size={20}/> : <Plus size={20}/>}
                        {formData.id ? 'Edit Company Profile' : 'Register New Company'}
                    </h3>
                    <form onSubmit={handleSubmit} style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px'}}>
                        
                        {/* LEFT COLUMN */}
                        <div>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Company Name</label>
                            <input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} style={inputStyle} required />
                            
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Type</label>
                            <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} style={inputStyle}>
                                <option value="CONT">Contractor</option>
                                <option value="SUB">Sub-Contractor</option>
                                <option value="SUPPLIER">Supplier</option>
                            </select>

                            {formData.type === 'SUB' && (
                                <div className="animate-fade-in">
                                    <label style={{display:'block', color:THEME.warning, fontSize:'0.8rem', marginBottom:'5px'}}>Link to Parent Contractor</label>
                                    <select 
                                        value={formData.parent_company} 
                                        onChange={e => setFormData({...formData, parent_company: e.target.value})} 
                                        style={{...inputStyle, border:`1px solid ${THEME.warning}`}}
                                        required
                                    >
                                        <option value="">-- Select Parent Contractor --</option>
                                        {potentialParents.map(parent => (
                                            <option key={parent.id} value={parent.id}>
                                                {parent.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* RIGHT COLUMN */}
                        <div>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Contact Person</label>
                            <div style={{position:'relative'}}>
                                <User size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/>
                                <input value={formData.contact_person} onChange={e => setFormData({...formData, contact_person: e.target.value})} style={{...inputStyle, paddingLeft:'35px'}} />
                            </div>
                            
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Email</label>
                            <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={inputStyle} />

                            {/* --- SITE ACCESS PILLS --- */}
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'8px'}}>Authorized Sites</label>
                            <div style={{display:'flex', flexWrap:'wrap', gap:'8px', padding:'10px', background:'#020617', borderRadius:'6px', border:`1px solid ${THEME.border}`}}>
                                {sites.length === 0 ? (
                                    <div style={{color:THEME.textSec, fontSize:'0.8rem', fontStyle:'italic'}}>No sites found.</div>
                                ) : (
                                    sites.map(site => {
                                        const isSelected = formData.site_ids.includes(site.id);
                                        return (
                                            <div 
                                                key={site.id} 
                                                onClick={() => toggleSite(site.id)}
                                                style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '20px',
                                                    fontSize: '0.85rem',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    background: isSelected ? THEME.primary : 'transparent',
                                                    color: isSelected ? 'white' : THEME.textSec,
                                                    border: `1px solid ${isSelected ? THEME.primary : THEME.border}`,
                                                    fontWeight: isSelected ? '600' : 'normal',
                                                    userSelect: 'none',
                                                    display: 'flex', alignItems: 'center', gap: '6px'
                                                }}
                                            >
                                                {site.code} {isSelected && <Check size={12}/>}
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                            <div style={{color:THEME.textSec, fontSize:'0.75rem', marginTop:'5px'}}>Click sites to grant access.</div>
                        </div>

                        <div style={{gridColumn:'span 2', display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'10px'}}>
                            <button type="button" onClick={() => setShowForm(false)} style={{padding:'10px 25px', background: 'transparent', color:THEME.textSec, border:`1px solid ${THEME.border}`, borderRadius:'6px', cursor:'pointer'}}>Cancel</button>
                            <button type="submit" style={{padding:'10px 25px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>
                                {formData.id ? 'Save Changes' : 'Create Record'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* GRID LAYOUT */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:'20px'}}>
                {filteredCompanies.map(c => (
                    <div 
                        key={c.id} 
                        style={{
                            background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, padding:'20px', 
                            display:'flex', flexDirection:'column', position:'relative', transition:'transform 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = THEME.primary}
                        onMouseLeave={e => e.currentTarget.style.borderColor = THEME.border}
                    >
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'start', marginBottom:'15px'}}>
                            <div style={{width:'40px', height:'40px', background:'#020617', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', border:`1px solid ${THEME.border}`}}>
                                {getTypeIcon(c.type)}
                            </div>
                            <ReadinessBadge status={c.readiness} />
                        </div>
                        
                        <h3 style={{color:'white', margin:'0 0 5px 0', fontSize:'1.1rem'}}>{c.name}</h3>
                        <div style={{color:THEME.textSec, fontSize:'0.85rem', marginBottom:'15px'}}>
                            {getTypeLabel(c.type)}
                        </div>

                        {c.parent_company_name && (
                             <div style={{color:THEME.warning, fontSize:'0.75rem', marginBottom:'10px', display:'flex', alignItems:'center', gap:'6px'}}>
                                <CornerDownRight size={14}/> Sub of: {c.parent_company_name}
                             </div>
                        )}

                        <div style={{borderTop:`1px solid ${THEME.border}`, paddingTop:'15px', marginTop:'auto'}}>
                            <div style={{display:'flex', alignItems:'center', gap:'10px', color: THEME.textSec, fontSize:'0.85rem', marginBottom:'5px'}}>
                                <User size={14}/> {c.contact_person || 'No Contact'}
                            </div>
                        </div>

                        <div style={{marginTop:'15px', display:'flex', gap:'10px'}}>
                            {/* EDIT BUTTON (Opens Modal) */}
                            <button 
                                onClick={(e) => { e.stopPropagation(); openEditForm(c); }}
                                style={{
                                    flex: 1, padding:'8px', background:'transparent', border:`1px solid ${THEME.textSec}`, color: THEME.textSec, 
                                    borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'0.85rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px'
                                }}
                            >
                                <Edit size={14}/> Edit
                            </button>
                            
                            {/* MANAGE BUTTON (Navigates to Profile) */}
                            <button 
                                onClick={(e) => { e.stopPropagation(); onSelectCompany(c); }}
                                style={{
                                    flex: 2, padding:'8px', background: THEME.primary, border:'none', color: 'white', 
                                    borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'0.85rem'
                                }}
                            >
                                Manage Profile
                            </button>

                            {/* DELETE BUTTON */}
                            {c.type !== 'OWNER' && (
                                <button 
                                    onClick={(e) => handleDelete(c.id, e)} 
                                    style={{
                                        background:'transparent', border:`1px solid ${THEME.danger}`, color: THEME.danger, 
                                        borderRadius:'6px', padding:'8px', cursor:'pointer'
                                    }}
                                    title="Delete Company"
                                >
                                    <Trash2 size={16}/>
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {filteredCompanies.length === 0 && (
                <div style={{textAlign:'center', padding:'40px', color:THEME.textSec}}>
                    No companies found.
                </div>
            )}
        </div>
    );
};

export default CompanyDirectory;